'use client';

// SDK shim surface: this context wires up Janus/Genesys browser SDKs
// whose callbacks are loosely typed (event payloads, plugin handles).
// Modeling them precisely would balloon the file without runtime gain.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { hasPermission as checkPermission, type Permission } from '@/lib/permissions';
import { apiUrl } from '@/lib/utils';
import { bumpCacheEpoch } from '@/lib/api-cache';
import type { PreviewTelephonyCall } from '@/lib/preview-telephony';
import { startPreviewAudioDiagnostics } from '@/lib/preview-audio-diagnostics';
import { useAmazonConnectWebRTCCall } from '@/hooks/use-amazon-connect-webrtc-call';

// ============================================================================
// Types
// ============================================================================

interface TenantInfo {
  id: string;
  slug: string;
  name: string;
  roles?: string[];
  permissions?: string[];
}

interface User {
  id: string;
  username: string;
  displayName: string;
  email?: string;
  roles: string[];
  permissions: string[];
  isPlatformAdmin?: boolean;
  activeTenant?: TenantInfo;
  tenants?: TenantInfo[];
}

interface SIPCredentials {
  sipUsername: string;
  sipPassword: string;
}

// Runtime config for SIP/WebRTC (fetched from server, not build-time env vars)
interface SIPRuntimeConfig {
  asteriskProxy: string;
  janusServer: string;
  // TURN server config for NAT traversal
  turnServer?: string;
  turnUsername?: string;
  turnCredential?: string;
  // When true, use TURN-over-TLS on port 443 only (for tunnel/proxy environments)
  turnTlsOnly?: boolean;
  // When true, force iceTransportPolicy='relay' (only TURN relay candidates)
  forceTurnOnly?: boolean;
  // Direct WebRTC endpoint for voicebot test calls (bypasses Janus/Asterisk)
  mediaProxyUrl?: string;
}

interface AuthContextType {
  // Authentication state
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  user: User | null;

  // Permission helpers
  hasPermission: (permission: Permission | string) => boolean;
  hasAnyPermission: (permissions: (Permission | string)[]) => boolean;

  // SIP/Janus state
  sipCredentials: SIPCredentials | null;
  isSipLoading: boolean;
  janusReady: boolean;
  isRegistered: boolean;
  isRegistering: boolean;
  isCalling: boolean;
  isInCall: boolean;
  callStartedAt: number | null;
  callStatus: string | null;
  callError: string | null;
  conversationId: string | null;
  /** Correlated observer ID; raw provider contacts never activate AI sessions. */
  telephonyConversationId: string | null;
  telephonyNotificationSessionId: string | null;
  setConversationId: (id: string | null) => void;
  remoteAnalyserNode: AnalyserNode | null;

  // Tenant state
  activeTenant: TenantInfo | null;
  tenants: TenantInfo[];
  isPlatformAdmin: boolean;
  switchTenant: (tenantId: string) => Promise<void>;

  // Auth actions
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;

  // SIP actions
  fetchSipCredentials: () => Promise<SIPCredentials | null>;
  registerSIP: () => Promise<boolean>;
  makeCall: (extension: string, options?: CallOptions) => Promise<void>;
  hangupCall: () => void;
  sendDTMF: (digit: string) => void;
  /** Send a JPEG frame over the WebRTC data channel (direct WebRTC calls only) */
  sendVideoFrame: (data: ArrayBuffer) => void;
  /**
   * Swap the live audio track on the in-progress call. Pass a `MediaStreamTrack`
   * to replace the mic with synthetic audio (used by phone-test type-to-speak),
   * or `null` to restore the original microphone capture.
   */
  replaceLocalAudio: (track: MediaStreamTrack | null) => Promise<boolean>;
  /**
   * Returns the current outgoing audio track (mic OR synth) for the active
   * call, or `null` if no call. Used to power a local-side waveform.
   */
  getLocalAudioTrack: () => MediaStreamTrack | null;
  /**
   * Returns the current incoming audio track for the active call, or `null`
   * if the remote side has not published audio yet. Used by demo recording.
   */
  getRemoteAudioTrack: () => MediaStreamTrack | null;

  // Refs for Janus
  janusRef: React.MutableRefObject<any>;
  sipcallRef: React.MutableRefObject<any>;
}

interface CallOptions {
  /** Live provider routing; never attaches a draft pipeline to the SIP leg. */
  telephony?: PreviewTelephonyCall;
  agentConfig?: any;
  customHeaders?: Record<string, string>;
  routingMode?: 'kamailio' | 'direct';
  /**
   * Optional pre-supplied audio track to use in place of the live microphone.
   * Used by phone-test "type to speak" mode to inject TTS-synthesized audio
   * into the same WebRTC peer the real mic would otherwise feed.
   */
  audioTrack?: MediaStreamTrack;
}

function withDemoNotificationSession(
  headers: Record<string, string> | undefined,
  conversationId: string,
): Record<string, string> {
  const next = { ...(headers || {}) };
  const delivery = Object.entries(next).find(
    ([name]) => name.toLowerCase() === 'x-multimodal-sms-delivery',
  )?.[1];
  if (delivery === 'demo_notification') {
    next['X-Demo-Notification-Session'] = conversationId;
  }
  return next;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// SCTP stream id for the pre-negotiated "video" data channel on the Direct WebRTC
// call. Must equal negotiatedVideoChannelID in
// apps/media-proxy/internal/webrtc/peer.go — a pre-negotiated channel has no
// in-band handshake, so a mismatch drops video frames silently while audio keeps
// working.
const NEGOTIATED_VIDEO_CHANNEL_ID = 0;

// How long the offer waits on ICE gathering before POSTing. The SDP already
// carries the ICE credentials and candidates keep trickling into the agent, so
// this only buys a fuller candidate list — keeping it short takes it off the
// call-startup critical path.
const ICE_GATHERING_WAIT_MS = 3000;

// ============================================================================
// Auth Provider
// ============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  // Tenant state
  const [activeTenant, setActiveTenant] = useState<TenantInfo | null>(null);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // SIP credentials (fetched on-demand)
  const [sipCredentials, setSipCredentials] = useState<SIPCredentials | null>(null);
  const [isSipLoading, setIsSipLoading] = useState(false);

  // Runtime config for SIP/WebRTC (fetched from server along with credentials)
  const sipRuntimeConfigRef = React.useRef<SIPRuntimeConfig | null>(null);

  // Janus/SIP state
  const [janusReady, setJanusReady] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
  const [callStatus, setCallStatus] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [telephonyConversationId, setTelephonyConversationId] = useState<string | null>(null);
  const [telephonySessionId, setTelephonySessionId] = useState<string | null>(null);
  const connectCall = useAmazonConnectWebRTCCall();
  const [connectActive, setConnectActive] = useState(false);
  const activeTelephonySessionId = connectActive ? connectCall.previewSessionId : telephonySessionId;
  const telephonyCallActive = connectActive
    ? ['calling', 'connected'].includes(connectCall.status) : isCalling || isInCall;
  useEffect(() => {
    if (!activeTelephonySessionId || !telephonyCallActive) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let failures = 0;
    const poll = async () => {
      let finished = false;
      try {
        const response = await fetch(`/api/preview/telephony/conversation?${new URLSearchParams({ sessionId: activeTelephonySessionId })}`, {
          cache: 'no-store', credentials: 'include',
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(8000)]),
        });
        if ([400, 401, 403, 404].includes(response.status)) finished = true;
        if (!response.ok) throw new Error('Could not connect Thinking to this telephony call.');
        const result = await response.json();
        if (controller.signal.aborted) return;
        failures = 0;
        if (typeof result.conversationId === 'string') {
          setTelephonyConversationId(result.conversationId);
          finished = true;
        }
      } catch {
        if (controller.signal.aborted) return;
        failures++;
        if (finished || failures === 3) toast.error('Could not connect Thinking to this telephony call.');
      } finally {
        if (!finished && !controller.signal.aborted) timer = setTimeout(poll, failures ? 5000 : 1000);
      }
    };
    void poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [activeTelephonySessionId, telephonyCallActive]);
  const [remoteAnalyserNode, setRemoteAnalyserNode] = useState<AnalyserNode | null>(null);
  const connectActiveRef = useRef(false);
  const connectCallRef = useRef(connectCall);
  useEffect(() => { connectCallRef.current = connectCall; }, [connectCall]);
  const sipAttemptRef = useRef(0);
  const sipPreparationRef = useRef<AbortController | null>(null);
  const sipAudioDiagnosticsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isCalling && !isInCall) {
      sipAudioDiagnosticsRef.current?.();
      sipAudioDiagnosticsRef.current = null;
    }
  }, [isCalling, isInCall]);
  useEffect(() => () => { sipAudioDiagnosticsRef.current?.(); }, []);

  // Refs
  const janusRef = React.useRef<any>(null);
  const sipcallRef = React.useRef<any>(null);
  const audioCtxRef = React.useRef<AudioContext | null>(null);
  const remoteAudioTrackRef = React.useRef<MediaStreamTrack | null>(null);
  const initRef = React.useRef<boolean>(false);
  const sessionCreatedRef = React.useRef<boolean>(false);
  const registrationHandledRef = React.useRef<boolean>(false);

  // Connection lock to prevent concurrent Janus session creation
  const janusConnectionPromiseRef = React.useRef<Promise<boolean> | null>(null);

  // Track whether the Janus session was successfully connected (to distinguish
  // post-connection errors like timeouts from initial connection failures)
  const janusConnectedOnceRef = React.useRef<boolean>(false);

  // Track whether a silent reconnection is in progress
  const reconnectingRef = React.useRef<boolean>(false);

  // Refs to keep callbacks stable while still accessing current state
  const isRegisteringRef = React.useRef(isRegistering);
  const isRegisteredRef = React.useRef(isRegistered);
  const isInCallRef = React.useRef(isInCall);
  const callConnected = isInCall || (connectActive && connectCall.status === 'connected');
  useEffect(() => {
    setCallStartedAt(callConnected ? Date.now() : null);
  }, [callConnected]);
  const isCallingRef = React.useRef(isCalling);
  const sipCredentialsRef = React.useRef(sipCredentials);
  useEffect(() => {
    isRegisteringRef.current = isRegistering;
    isRegisteredRef.current = isRegistered;
    isInCallRef.current = callConnected;
    isCallingRef.current = isCalling || (connectActive && connectCall.status === 'calling');
    sipCredentialsRef.current = sipCredentials;
  }, [isRegistering, isRegistered, callConnected, isCalling, connectActive, connectCall.status, sipCredentials]);

  // ============================================================================
  // Global 401 interceptor — wrap window.fetch so any API response that
  // indicates an expired session surfaces exactly once as a toast and
  // redirects to /login with a returnTo. Without this the app silently
  // leaks 401s into random UI breakages.
  //
  // Why a global wrapper (and not per-call handling)? There are hundreds
  // of fetch call sites; each would need to check status 401 and toast.
  // A single wrapper keeps all those paths DRY and also gives us a single
  // knob to add refresh-token retries later if SSO gets a silent-refresh
  // endpoint.
  // ============================================================================

  const sessionExpiredRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const originalFetch = window.fetch.bind(window);
    const handler: typeof fetch = async (input, init) => {
      const res = await originalFetch(input, init);
      if (res.status !== 401) return res;

      // Only react to 401 from our own /api/** surface. A third-party API we
      // hit with bad creds shouldn't kick the user to the login page.
      let urlStr: string;
      if (typeof input === 'string') urlStr = input;
      else if (input instanceof URL) urlStr = input.toString();
      else if (input instanceof Request) urlStr = input.url;
      else urlStr = '';

      let isOwnApi = false;
      try {
        const u = new URL(urlStr, window.location.origin);
        if (u.origin === window.location.origin && u.pathname.startsWith('/api/')) {
          isOwnApi = true;
        }
      } catch {
        // Relative URLs not starting with http(s) still land here.
        if (urlStr.startsWith('/api/')) isOwnApi = true;
      }
      if (!isOwnApi) return res;

      // /api/auth/session and /api/auth/login are expected to 401 during
      // normal unauthenticated bootstraps — don't kick the user for those.
      if (
        urlStr.includes('/api/auth/session') ||
        urlStr.includes('/api/auth/login') ||
        urlStr.includes('/api/scene-stages/previews/')
      ) {
        return res;
      }

      if (!sessionExpiredRef.current) {
        sessionExpiredRef.current = true;
        toast.error(
          'Your session has expired. Redirecting to login…',
          { duration: 4_000 },
        );
        // Store the current path so /login can restore it after SSO round-trip.
        try {
          const currentPath = window.location.pathname + window.location.search + window.location.hash;
          sessionStorage.setItem('redirectAfterLogin', currentPath);
        } catch {
          // sessionStorage can throw in incognito/3p-cookie edge cases — fine.
        }
        // Small delay so the toast is visible before we navigate.
        setTimeout(() => {
          window.location.href = '/login';
        }, 800);
      }
      return res;
    };
    window.fetch = handler as typeof window.fetch;
    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // ============================================================================
  // Session Check on Mount
  // ============================================================================

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch(apiUrl('/api/auth/session'), {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.authenticated && data.user) {
            setUser(data.user);
            setIsAuthenticated(true);
            // Populate tenant state from session response
            if (data.user.activeTenant) {
              setActiveTenant(data.user.activeTenant);
            }
            if (data.user.tenants) {
              setTenants(data.user.tenants);
            }
            if (data.user.isPlatformAdmin) {
              setIsPlatformAdmin(true);
            }

            // Restore the user's previously-chosen tenant on hard refresh.
            // The server falls back to tenants[0] when its session row has
            // no active_tenant_id (e.g. after the SSO session expired and
            // a new one was created). Without this the user silently gets
            // bumped into a different tenant's data on every SSO round-trip.
            try {
              const storedTenantId = localStorage.getItem('activeTenantId');
              const currentTenantId = data.user.activeTenant?.id;
              if (
                storedTenantId &&
                currentTenantId &&
                storedTenantId !== currentTenantId &&
                (data.user.tenants as TenantInfo[] | undefined)?.some(
                  (t) => t.id === storedTenantId,
                )
              ) {
                // Silent re-switch — hit the server to update active_tenant_id
                // but don't full-reload; the UI is already on the right page.
                const switchRes = await fetch(apiUrl('/api/auth/switch-tenant'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ tenantId: storedTenantId }),
                  credentials: 'include',
                });
                if (switchRes.ok) {
                  const switched = await switchRes.json();
                  setUser(switched.user);
                  if (switched.user.activeTenant) {
                    setActiveTenant(switched.user.activeTenant);
                  }
                  if (switched.user.tenants) {
                    setTenants(switched.user.tenants);
                  }
                  setIsPlatformAdmin(Boolean(switched.user.isPlatformAdmin));
                }
              } else if (currentTenantId) {
                // First-time session with no stored pref — remember what the
                // server picked so future refreshes round-trip consistently.
                localStorage.setItem('activeTenantId', currentTenantId);
              }
            } catch (err) {
              // localStorage can throw in locked-down browsers. Non-fatal.
              console.warn('[AuthContext] tenant preference restore failed:', err);
            }
          }
        }
      } catch (error) {
        console.error('[AuthContext] Session check failed:', error);
      } finally {
        setIsAuthLoading(false);
      }
    };

    checkSession();
  }, []);

  // ============================================================================
  // Session Keepalive — refresh the session periodically while the tab is active
  // to prevent expiration during long editing sessions.
  // ============================================================================

  useEffect(() => {
    if (!isAuthenticated) return;

    const KEEPALIVE_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

    const refreshSession = async () => {
      // Only refresh when the tab is visible
      if (document.hidden) return;
      try {
        const response = await fetch(apiUrl('/api/auth/session'), {
          credentials: 'include',
        });
        if (response.status === 401) {
          // Session genuinely expired. Kick through the same toast+redirect
          // flow the 401 interceptor uses (it's already armed against
          // /api/auth/session, so fire it ourselves here).
          if (!sessionExpiredRef.current) {
            sessionExpiredRef.current = true;
            toast.error(
              'Your session has expired. Redirecting to login…',
              { duration: 4_000 },
            );
            try {
              const currentPath = window.location.pathname + window.location.search + window.location.hash;
              sessionStorage.setItem('redirectAfterLogin', currentPath);
            } catch {
              /* noop */
            }
            setTimeout(() => {
              window.location.href = '/login';
            }, 800);
          }
        } else if (!response.ok) {
          console.warn('[AuthContext] Session keepalive returned', response.status);
        }
      } catch (error) {
        console.error('[AuthContext] Session keepalive failed:', error);
      }
    };

    const intervalId = setInterval(refreshSession, KEEPALIVE_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // ============================================================================
  // Load Janus Library
  // ============================================================================

  // Cleanup function to destroy Janus session
  const cleanupJanus = useCallback((resetState = true) => {
    console.log('[AuthContext] 🧹 Cleaning up Janus session...');
    if (sipcallRef.current) {
      try {
        sipcallRef.current.hangup?.();
        sipcallRef.current.detach?.();
      } catch (e) {
        console.debug('[AuthContext] sipcall cleanup error (expected):', e);
      }
      sipcallRef.current = null;
    }
    if (janusRef.current) {
      try {
        janusRef.current.destroy?.();
      } catch (e) {
        console.debug('[AuthContext] janus cleanup error (expected):', e);
      }
      janusRef.current = null;
    }
    sessionCreatedRef.current = false;
    janusConnectionPromiseRef.current = null;
    registrationHandledRef.current = false;
    janusConnectedOnceRef.current = false;
    if (resetState) {
      setIsRegistered(false);
      setIsRegistering(false);
      setIsCalling(false);
      setIsInCall(false);
      setCallStatus(null);
    }
  }, []);

  // Ref-based silent reconnection to avoid circular useCallback dependencies
  // (silentReconnect needs registerSIP, and registerSIP's Janus constructor
  // callbacks need silentReconnect).
  const silentReconnectRef = React.useRef<() => void>(() => { });

  const silentReconnect = useCallback(() => {
    silentReconnectRef.current();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    console.log('[AuthContext] 🚀 Starting Janus initialization...');

    // beforeunload can run synchronously inside Next's Router render and can
    // be cancelled. Release media only once the document actually leaves;
    // there is no UI to update at that point.
    const handlePageHide = () => {
      cleanupJanus(false);
    };

    // A back/forward-cache restore revives the old React tree after pagehide
    // released its media. Reset the UI so the normal registration effect runs.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) cleanupJanus();
    };

    // When the tab becomes visible again, check if the Janus session is still
    // alive and silently re-register if it timed out while backgrounded.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.debug('[AuthContext] Tab hidden');
      } else {
        console.debug('[AuthContext] Tab visible - checking Janus session health');
        if (janusConnectedOnceRef.current && janusRef.current) {
          // Check if the WebSocket connection is still alive
          const isConnected = janusRef.current.isConnected?.();
          if (!isConnected) {
            console.log('[AuthContext] Janus session stale after tab restore, reconnecting...');
            silentReconnect();
          }
        }
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const initJanus = () => {
      if (!window.adapter) return;
      console.log('[AuthContext] initJanus called', {
        initRefCurrent: initRef.current,
        hasJanus: !!window.Janus,
        hasJanusInit: !!(window.Janus && window.Janus.init)
      });

      if (initRef.current) {
        console.log('[AuthContext] Already initialized, skipping');
        return;
      }

      if (window.Janus && window.Janus.init) {
        initRef.current = true;
        console.log('[AuthContext] Calling Janus.init()...');
        window.Janus.init({
          debug: 'all',
          callback: () => {
            console.log('[AuthContext] ✅ Janus library initialized successfully');
            setJanusReady(true);
          }
        });
      } else {
        console.log('[AuthContext] Janus not ready yet, retrying in 100ms...');
        setTimeout(() => {
          if (window.Janus && !initRef.current) {
            initJanus();
          }
        }, 100);
      }
    };

    const loadJanus = () => {
      // Strict Mode can revisit an adapter script that is still downloading.
      // The fallback timer must not load Janus before that dependency is ready.
      if (!window.adapter) return;
      const existingScript = document.querySelector('script[src="/janus-demos/janus.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', initJanus);
        setTimeout(initJanus, 500);
      } else {
        const script = document.createElement('script');
        script.src = '/janus-demos/janus.js';
        script.async = true;
        script.onload = () => {
          console.log('Janus script loaded');
          setTimeout(initJanus, 100);
        };
        script.onerror = () => {
          toast.error('Failed to load Janus library');
        };
        document.head.appendChild(script);
      }
    };

    if (!window.adapter) {
      const existingAdapter = document.querySelector('script[src*="adapter"]');
      if (existingAdapter) {
        existingAdapter.addEventListener('load', loadJanus);
        setTimeout(loadJanus, 500);
      } else {
        const adapterScript = document.createElement('script');
        adapterScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/webrtc-adapter/8.2.2/adapter.min.js';
        adapterScript.async = true;
        adapterScript.onload = () => {
          console.log('WebRTC adapter loaded');
          loadJanus();
        };
        adapterScript.onerror = () => {
          toast.error('Failed to load WebRTC adapter');
        };
        document.head.appendChild(adapterScript);
      }
    } else {
      loadJanus();
    }

    if (window.Janus) {
      initJanus();
    }

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanupJanus();
    };
  }, [cleanupJanus, silentReconnect]);

  // ============================================================================
  // Mobile Audio Autoplay Handler
  // ============================================================================

  useEffect(() => {
    // Skip on server-side rendering
    if (typeof window === 'undefined') return;

    const handleUserInteraction = async () => {
      const pendingAudio = (window as any)._pendingAudioElement;
      if (pendingAudio && pendingAudio.paused) {
        try {
          await pendingAudio.play();
          console.log('Resumed audio playback after user interaction');
          (window as any)._pendingAudioElement = null;
        } catch (err) {
          console.error('Failed to resume audio:', err);
        }
      }

      document.querySelectorAll('[id^="remote-audio-"]').forEach((el) => {
        const audioEl = el as HTMLAudioElement;
        if (audioEl.paused && audioEl.srcObject) {
          audioEl.play().catch((err) => {
            console.debug('Could not resume audio element:', err);
          });
        }
      });
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  // ============================================================================
  // Permission Helpers
  // ============================================================================

  const hasPermission = useCallback((permission: Permission | string): boolean => {
    if (!user?.permissions) return false;
    return user.isPlatformAdmin === true || checkPermission(user.permissions, permission as Permission);
  }, [user]);

  const hasAnyPermission = useCallback((permissions: (Permission | string)[]): boolean => {
    return permissions.some(hasPermission);
  }, [hasPermission]);

  // ============================================================================
  // Fetch SIP Credentials
  // ============================================================================

  // Use a ref to track the in-progress fetch promise to avoid race conditions
  const sipFetchPromiseRef = useRef<Promise<SIPCredentials | null> | null>(null);

  const fetchSipCredentials = useCallback(async (): Promise<SIPCredentials | null> => {
    // Use ref to get current value without causing callback recreation
    if (sipCredentialsRef.current) {
      return sipCredentialsRef.current;
    }

    // If there's already a fetch in progress, wait for it
    if (sipFetchPromiseRef.current) {
      return sipFetchPromiseRef.current;
    }

    setIsSipLoading(true);

    // Create and store the promise so concurrent calls can wait for it
    const fetchPromise = (async (): Promise<SIPCredentials | null> => {
      try {
        const response = await fetch(apiUrl('/api/auth/sip-credentials'), {
          credentials: 'include',
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('[AuthContext] Failed to fetch SIP credentials:', error);
          toast.error(error.error || 'Failed to fetch SIP credentials');
          return null;
        }

        const data = await response.json();
        const creds: SIPCredentials = {
          sipUsername: data.sipUsername,
          sipPassword: data.sipPassword,
        };
        setSipCredentials(creds);

        // Store runtime config from server (allows K8s deployments to configure SIP/Janus addresses)
        if (data.config) {
          sipRuntimeConfigRef.current = data.config;
          console.log('[AuthContext] Runtime SIP config received:', {
            asteriskProxy: data.config.asteriskProxy || '(not set)',
            janusServer: data.config.janusServer || '(not set)',
          });
        }

        return creds;
      } catch (error) {
        console.error('[AuthContext] Error fetching SIP credentials:', error);
        toast.error('Failed to fetch SIP credentials');
        return null;
      } finally {
        setIsSipLoading(false);
        sipFetchPromiseRef.current = null;
      }
    })();

    sipFetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, []); // Stable - uses ref for sipCredentials

  // ============================================================================
  // SIP Registration
  // ============================================================================

  const registerSIP = useCallback(async (): Promise<boolean> => {
    console.log('[AuthContext] 📞 registerSIP() called');

    // If there's already a connection attempt in progress, wait for it
    if (janusConnectionPromiseRef.current) {
      console.log('[AuthContext] ⏳ Connection already in progress, waiting for it...');
      return janusConnectionPromiseRef.current;
    }

    // Prevent multiple concurrent registration attempts (use ref to get current value)
    if (isRegisteringRef.current) {
      console.log('[AuthContext] ⏳ Registration already in progress, skipping');
      return false;
    }

    // If already registered with a valid session, no need to re-register
    // Use a more reliable check - verify both the session and the plugin handle exist
    const hasValidSession = janusRef.current && sipcallRef.current && sessionCreatedRef.current;
    if (isRegisteredRef.current && hasValidSession) {
      console.log('[AuthContext] ✅ Already registered with active session');
      return true;
    }

    // Clean up any existing session before creating new one
    // We can't reuse stale sessions because the SIP plugin keeps auth state
    // that causes "Operation has no matching challenge" errors (code 904)
    if (sessionCreatedRef.current || janusRef.current || sipcallRef.current) {
      console.log('[AuthContext] 🧹 Cleaning up partial session state before creating new one');
      cleanupJanus();
    }

    if (!window.Janus || !window.Janus.init) {
      console.error('[AuthContext] ❌ Janus library not available');
      toast.error('Janus library not loaded. Please refresh the page.');
      return false;
    }

    if (!window.Janus.isWebrtcSupported || !window.Janus.isWebrtcSupported()) {
      console.error('[AuthContext] ❌ WebRTC not supported');
      toast.error('WebRTC not supported in this browser');
      return false;
    }

    console.log('[AuthContext] ✅ WebRTC is supported');
    setIsRegistering(true);
    registrationHandledRef.current = false;

    // Create and store the connection promise early so concurrent calls can wait for it
    const connectionPromise = new Promise<boolean>((resolve) => {
      (async () => {
        // Get SIP credentials first
        console.log('[AuthContext] Fetching SIP credentials...');
        const creds = await fetchSipCredentials();
        if (!creds) {
          console.error('[AuthContext] ❌ Could not get SIP credentials');
          toast.error('Could not get SIP credentials');
          setIsRegistering(false);
          janusConnectionPromiseRef.current = null;
          resolve(false);
          return;
        }

        const { sipUsername, sipPassword } = creds;

        console.log('[AuthContext] registerSIP state:', {
          janusReady,
          hasJanus: !!window.Janus,
          sipUsername,
          sessionCreatedRef: sessionCreatedRef.current,
        });

        let registrationStarted = false;
        const timeout = setTimeout(() => {
          console.error('[AuthContext] ❌ SIP registration timeout after 15 seconds', {
            sessionCreated: sessionCreatedRef.current,
            hasSipcall: !!sipcallRef.current,
            registrationStarted
          });
          setIsRegistering(false);
          janusConnectionPromiseRef.current = null;
          if (!sessionCreatedRef.current || !sipcallRef.current) {
            toast.error('SIP registration timeout: Could not connect to Janus server.');
          } else if (!registrationStarted) {
            toast.error('SIP registration timeout: Registration request not sent.');
          } else {
            toast.error('SIP registration timeout: No response from Asterisk server.');
          }
          resolve(false);
        }, 15000);

        try {
          // Use WebSocket transport for real-time communication with Janus
          // Priority: 1) Runtime config from server, 2) NEXT_PUBLIC_* build-time vars, 3) derived from hostname
          const hostname = window.location.hostname;
          const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

          // Get runtime config (populated by fetchSIPCredentials)
          const runtimeConfig = sipRuntimeConfigRef.current;

          let server: string;
          if (runtimeConfig?.janusServer) {
            // Runtime config from server — the API computes the correct URL
            // for the current environment (tunnel, K8s, local, etc.)
            server = runtimeConfig.janusServer;
          } else if (process.env.NEXT_PUBLIC_JANUS_SERVER) {
            // Build-time env var (local dev)
            server = process.env.NEXT_PUBLIC_JANUS_SERVER;
          } else if (isLocalhost) {
            // Local development without explicit config - assume Janus is also local
            server = `ws://${hostname}:8188/janus`;
          } else {
            // K8s deployment fallback - use subdomain routing through gateway
            const parts = hostname.split('.');
            const baseDomain = parts.slice(1).join('.');
            server = `wss://janus.${baseDomain}/janus`;
          }

          console.log('[AuthContext] Creating Janus session to:', server);

          const turnServer = runtimeConfig?.turnServer || process.env.NEXT_PUBLIC_TURN_SERVER || '';
          const turnUsername = runtimeConfig?.turnUsername || '';
          const turnCredential = runtimeConfig?.turnCredential || '';
          console.log('[AuthContext] TURN configured:', !!(turnServer && turnUsername && turnCredential));

          const opaqueId = 'ai-manager-' + window.Janus.randomString(12);

          // Build iceServers configuration with explicit credentials.
          // When turnTlsOnly is set (via env var TURN_TLS_ONLY=true), only
          // use TURN-over-TLS on port 443. This is needed in tunnel/proxy
          // environments where the proxy terminates TLS on 443 and forwards
          // raw TCP to coturn — UDP and direct TCP won't traverse the proxy.
          const turnTlsOnly = runtimeConfig?.turnTlsOnly || process.env.NEXT_PUBLIC_TURN_TLS_ONLY === 'true';
          const iceServersConfig: RTCIceServer[] = !(turnServer && turnUsername && turnCredential)
            ? [{ urls: "stun:stun.l.google.com:19302" }]
            : turnTlsOnly
            ? [
              {
                urls: `turns:${turnServer}:443?transport=tcp`,
                username: turnUsername,
                credential: turnCredential
              }
            ]
            : [
              { urls: `stun:${turnServer}:3478` },
              {
                urls: `turn:${turnServer}:3478`,
                username: turnUsername,
                credential: turnCredential
              },
              {
                urls: `turn:${turnServer}:3478?transport=tcp`,
                username: turnUsername,
                credential: turnCredential
              },
              {
                urls: `turns:${turnServer}:5349?transport=tcp`,
                username: turnUsername,
                credential: turnCredential
              }
            ];

          // Debug: Log full iceServers configuration to verify credentials are set
          console.log('[AuthContext] 🔧 ICE servers configuration:');
          iceServersConfig.forEach((server, i) => {
            const hasUsername = !!server.username;
            const hasCredential = !!server.credential;
            console.log(`[AuthContext] 🔧 Server ${i}:`, {
              urls: server.urls,
              hasUsername,
              hasCredential,
              credentialLength: server.credential?.length || 0
            });
          });

          // Force TURN-only mode (iceTransportPolicy='relay') — only gather relay candidates.
          // Useful when Janus and coturn are co-located (e.g. vanguard) so relay always works.
          // Defaults to false so ICE can try host/srflx candidates in other environments.
          const forceTurnOnly = runtimeConfig?.forceTurnOnly ??
            process.env.NEXT_PUBLIC_FORCE_TURN_ONLY === 'true';
          if (forceTurnOnly) {
            console.log('[AuthContext] ⚠️ TURN-only mode ENABLED - forcing relay transport');
          }

          let session: any = null;
          session = new window.Janus({
            server: server,
            iceServers: iceServersConfig,
            iceTransportPolicy: forceTurnOnly ? 'relay' : undefined,
            // Called when the Janus gateway times out the session (e.g., browser tab backgrounded).
            // janus.js closes the WebSocket right after this callback, which triggers the
            // error callback below - that's where we do the actual reconnection.
            timeout: () => {
              console.log('[AuthContext] Janus session timed out - will reconnect after WS closes');
            },
            success: () => {
              // A navigation/reconnect may have disposed of this session while
              // the gateway was answering. Never attach through a newer ref.
              if (janusRef.current !== session) {
                clearTimeout(timeout);
                session.destroy?.();
                resolve(false);
                return;
              }
              console.log('[AuthContext] ✅ Janus session created successfully');
              sessionCreatedRef.current = true;
              janusConnectedOnceRef.current = true;
              console.log('[AuthContext] Attaching SIP plugin...');
              session.attach({
                plugin: 'janus.plugin.sip',
                opaqueId: opaqueId,
                success: (pluginHandle: any) => {
                  if (janusRef.current !== session) {
                    clearTimeout(timeout);
                    pluginHandle.detach?.();
                    resolve(false);
                    return;
                  }
                  sipcallRef.current = pluginHandle;
                  console.log('[AuthContext] ✅ SIP plugin attached');

                  const externalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
                    ? '127.0.0.1'
                    : window.location.hostname;

                  // Get Asterisk proxy from runtime config (K8s) or build-time env var (local dev)
                  const asteriskProxyEnv = runtimeConfig?.asteriskProxy || process.env.NEXT_PUBLIC_ASTERISK_PROXY || '';

                  let asteriskProxy: string;
                  let asteriskPort: string;
                  if (asteriskProxyEnv) {
                    // Parse the proxy string: can be "sip:host:port", "host:port", or just "host"
                    const clean = asteriskProxyEnv.replace(/^sip:/, '');
                    const parts = clean.split(':');
                    asteriskProxy = parts[0];
                    asteriskPort = parts[1] || '5063';
                  } else {
                    asteriskProxy = '127.0.0.1';
                    asteriskPort = '5063';
                  }
                  console.log('[AuthContext] Asterisk proxy:', `${asteriskProxy}:${asteriskPort}`);

                  const sipConfig = {
                    server: `sip:${asteriskProxy}:${asteriskPort}`,
                    username: `sip:${sipUsername}@${externalHost}`,
                    authuser: sipUsername,
                    displayname: user?.displayName || sipUsername,
                    secret: sipPassword,
                    domain: externalHost
                  };

                  console.log('Attempting SIP registration with config:', {
                    server: sipConfig.server,
                    username: sipConfig.username,
                    authuser: sipConfig.authuser,
                    domain: sipConfig.domain
                  });

                  const register = {
                    request: 'register',
                    username: sipConfig.username,
                    secret: sipConfig.secret,
                    display_name: sipConfig.displayname,
                    proxy: sipConfig.server,
                    // Include fallback codecs - Janus SIP plugin needs these for proper
                    // SDP generation. Opus is preferred but PCMU/PCMA are fallbacks.
                    codecs: ['opus', 'pcmu', 'pcma']
                  };

                  registrationStarted = true;
                  sipcallRef.current.send({ message: register });

                  // Set up message handler
                  sipcallRef.current.onmessage = (msg: any, jsep: any) => {
                    console.log('SIP plugin message:', JSON.stringify(msg, null, 2), jsep);

                    if (msg.error) {
                      console.error('SIP plugin error response:', JSON.stringify(msg.error, null, 2));
                      clearTimeout(timeout);
                      setIsRegistered(false);
                      setIsRegistering(false);
                      janusConnectionPromiseRef.current = null;
                      const errorReason = msg.error.reason || msg.error.message || JSON.stringify(msg.error);
                      toast.error(`SIP registration failed: ${errorReason}`);
                      resolve(false);
                      return;
                    }

                    const result = msg.result;
                    if (result) {
                      const event = result.event;

                      if (event === 'registered') {
                        if (registrationHandledRef.current) {
                          console.debug('[AuthContext] Duplicate registered event ignored');
                          return;
                        }
                        registrationHandledRef.current = true;
                        console.log('SIP registered successfully');
                        clearTimeout(timeout);
                        setIsRegistered(true);
                        setIsRegistering(false);
                        janusConnectionPromiseRef.current = null;
                        resolve(true);
                      } else if (event === 'registration_failed') {
                        if (registrationHandledRef.current) {
                          console.debug('[AuthContext] Duplicate registration_failed event ignored');
                          return;
                        }
                        registrationHandledRef.current = true;
                        console.error('SIP registration failed:', JSON.stringify(result, null, 2));
                        clearTimeout(timeout);
                        setIsRegistered(false);
                        setIsRegistering(false);
                        janusConnectionPromiseRef.current = null;
                        const reason = result.reason || result.code || 'Unknown error';
                        toast.error(`SIP registration failed: ${reason}`);
                        resolve(false);
                      } else if (event === 'calling') {
                        setIsCalling(true);
                        setIsInCall(false);
                        setCallStatus('Calling...');
                      } else if (event === 'progress') {
                        if (sipcallRef.current && (sipcallRef.current as any)._callTimeout) {
                          clearTimeout((sipcallRef.current as any)._callTimeout);
                          delete (sipcallRef.current as any)._callTimeout;
                        }
                        setCallStatus('Connecting...');
                        if (jsep && sipcallRef.current) {
                          sipcallRef.current.handleRemoteJsep({ jsep: jsep });
                        }
                      } else if (event === 'accepted') {
                        if (sipcallRef.current && (sipcallRef.current as any)._callTimeout) {
                          clearTimeout((sipcallRef.current as any)._callTimeout);
                          delete (sipcallRef.current as any)._callTimeout;
                        }
                        setIsCalling(false);
                        setIsInCall(true);
                        setCallStatus('Call in progress');
                        if (jsep && sipcallRef.current) {
                          sipcallRef.current.handleRemoteJsep({ jsep: jsep });
                        }
                      } else if (event === 'updatingcall' && jsep && sipcallRef.current && !connectActiveRef.current && (isInCallRef.current || isCallingRef.current)) {
                        // Hold/resume and IVR→agent transfers can renegotiate the
                        // existing SIP leg. Keep its tracks, timer, and identity.
                        const handle = sipcallRef.current;
                        const attempt = sipAttemptRef.current;
                        handle.createAnswer({
                          jsep,
                          tracks: [],
                          success: (answer: any) => {
                            if (attempt === sipAttemptRef.current && handle === sipcallRef.current) {
                              handle.send({ message: { request: 'update' }, jsep: answer });
                            }
                          },
                          error: () => {
                            if (attempt === sipAttemptRef.current) toast.error('Could not update call audio after hold or transfer.');
                          },
                        });
                      } else if (event === 'updated' && jsep?.type === 'answer') {
                        sipcallRef.current?.handleRemoteJsep({ jsep });
                      } else if (event === 'hangup' || event === 'timeout' || event === 'failed') {
                        sipAttemptRef.current += 1;
                        sipPreparationRef.current?.abort();
                        if (sipcallRef.current && (sipcallRef.current as any)._callTimeout) {
                          clearTimeout((sipcallRef.current as any)._callTimeout);
                          delete (sipcallRef.current as any)._callTimeout;
                        }
                        setIsCalling(false);
                        setIsInCall(false);
                        setCallStatus(null);
                        setConversationId(null);
                        if (event === 'timeout' || event === 'failed') {
                          toast.error(`Call ${event}: ${result?.reason || 'No response'}`);
                        }
                        document.querySelectorAll('[id^="remote-audio-"]').forEach(el => el.remove());
                        remoteAudioTrackRef.current = null;
                        if (sipcallRef.current && sipcallRef.current.hangup) {
                          sipcallRef.current.hangup();
                        }
                      }
                    }
                  };

                  // Set up local track handler for debugging
                  sipcallRef.current.onlocaltrack = (track: MediaStreamTrack, on: boolean) => {
                    console.log('[AuthContext] 🎤 Local track', on ? 'ADDED' : 'REMOVED', track);
                    if (on && track) {
                      console.log('[AuthContext] 🎤 Local track details:', {
                        kind: track.kind,
                        id: track.id,
                        label: track.label,
                        enabled: track.enabled,
                        muted: track.muted,
                        readyState: track.readyState
                      });
                    }
                  };

                  // Set up remote track handler
                  sipcallRef.current.onremotetrack = (track: MediaStreamTrack, mid: string, on: boolean) => {
                    console.log('Remote track (mid=' + mid + ')', on ? 'added' : 'removed', track);

                    if (!on) {
                      const audioEl = document.getElementById(`remote-audio-${mid}`);
                      if (audioEl) {
                        audioEl.remove();
                      }
                      if (remoteAudioTrackRef.current?.id === track.id) {
                        remoteAudioTrackRef.current = null;
                      }
                      return;
                    }

                    if (track.kind === 'audio') {
                      remoteAudioTrackRef.current = track;
                      const stream = new MediaStream([track]);

                      let audioEl = document.getElementById(`remote-audio-${mid}`) as HTMLAudioElement;
                      if (!audioEl) {
                        audioEl = document.createElement('audio');
                        audioEl.id = `remote-audio-${mid}`;
                        audioEl.autoplay = true;
                        audioEl.setAttribute('playsInline', 'true');
                        audioEl.className = 'hidden';
                        document.body.appendChild(audioEl);
                      }

                      if (window.Janus && window.Janus.attachMediaStream) {
                        window.Janus.attachMediaStream(audioEl, stream);
                      } else {
                        audioEl.srcObject = stream;
                      }

                      audioEl.volume = 1.0;
                      audioEl.muted = false;

                      const playAudio = async () => {
                        try {
                          await audioEl.play();
                        } catch (err: any) {
                          if (err.name === 'NotAllowedError') {
                            console.warn('Audio playback blocked. Please tap the page to enable audio.');
                            (window as any)._pendingAudioElement = audioEl;
                          }
                        }
                      };

                      setTimeout(playAudio, 50);
                    }
                  };
                },
                error: (err: any) => {
                  console.error('Failed to attach SIP plugin:', err);
                  clearTimeout(timeout);
                  setIsRegistering(false);
                  janusConnectionPromiseRef.current = null;
                  toast.error(`Failed to attach SIP plugin: ${err.message || err}`);
                  resolve(false);
                }
              });
            },
            error: (err: any) => {
              if (janusRef.current !== session) {
                clearTimeout(timeout);
                resolve(false);
                return;
              }
              const errMsg = err?.message || err || '';
              // If the session was previously connected, this is a post-connection
              // error (e.g., WebSocket closed after session timeout). Silently reconnect
              // instead of showing an error toast to the user.
              if (janusConnectedOnceRef.current) {
                console.log('[AuthContext] Janus connection lost (post-connect):', errMsg);
                silentReconnect();
                return;
              }
              // Initial connection failure - show error to user
              console.error('Failed to create Janus session:', err);
              clearTimeout(timeout);
              setIsRegistering(false);
              janusConnectionPromiseRef.current = null;
              toast.error(`Failed to connect to Janus: ${errMsg}`);
              resolve(false);
            }
          });
          janusRef.current = session;
        } catch (error: any) {
          console.error('Registration error:', error);
          clearTimeout(timeout);
          setIsRegistering(false);
          janusConnectionPromiseRef.current = null;
          toast.error(`Registration failed: ${error.message || error}`);
          resolve(false);
        }
      })();
    });

    janusConnectionPromiseRef.current = connectionPromise;
    return connectionPromise;
  }, [fetchSipCredentials, janusReady, user, cleanupJanus, silentReconnect]); // Removed isRegistering/isRegistered - using refs for stability

  // Update the silentReconnect implementation now that registerSIP is defined.
  // This breaks the circular useCallback dependency.
  useEffect(() => {
    silentReconnectRef.current = () => {
      if (reconnectingRef.current) {
        console.debug('[AuthContext] Silent reconnect already in progress, skipping');
        return;
      }
      // Don't reconnect during an active call - the call is already lost
      if (isInCallRef.current || isCallingRef.current) {
        console.warn('[AuthContext] Session lost during active call - cannot silently reconnect');
        return;
      }
      // Only reconnect if we were previously registered
      if (!janusConnectedOnceRef.current) {
        return;
      }

      reconnectingRef.current = true;
      console.log('[AuthContext] Starting silent reconnection...');

      // Clean up the stale session state
      cleanupJanus();

      // Re-register after a short delay to let cleanup complete
      setTimeout(async () => {
        try {
          const success = await registerSIP();
          if (success) {
            console.log('[AuthContext] Silent reconnection successful');
          } else {
            console.warn('[AuthContext] Silent reconnection failed');
          }
        } catch (err) {
          console.warn('[AuthContext] Silent reconnection error:', err);
        } finally {
          reconnectingRef.current = false;
        }
      }, 500);
    };
  }, [cleanupJanus, registerSIP]);

  // ============================================================================
  // Login
  // ============================================================================

  const login = useCallback(async (username: string, password: string) => {
    const response = await fetch(apiUrl('/api/auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    setUser(data.user);
    setIsAuthenticated(true);

    // Populate tenant state from login response
    if (data.user.activeTenant) {
      setActiveTenant(data.user.activeTenant);
      // Remember the chosen tenant across refreshes. See the session-check
      // effect for why this matters.
      try {
        localStorage.setItem('activeTenantId', data.user.activeTenant.id);
      } catch {
        /* noop */
      }
    }
    if (data.user.tenants) {
      setTenants(data.user.tenants);
    }
    if (data.user.isPlatformAdmin) {
      setIsPlatformAdmin(true);
    }

    // Don't auto-register SIP here - let the user explicitly do it when they need to make calls
    // This keeps the login flow fast and doesn't require SIP permission for basic app access
  }, []);

  // ============================================================================
  // Direct WebRTC refs + hangup (must be before hangupCall)
  // ============================================================================

  const directPcRef = React.useRef<RTCPeerConnection | null>(null);
  const directSessionIdRef = React.useRef<string | null>(null);
  const directAttemptRef = React.useRef(0);
  const directCallActiveRef = React.useRef(false);
  const directCleanupRef = React.useRef<(() => void) | null>(null);
  const videoDCRef = React.useRef<RTCDataChannel | null>(null);
  // Original microphone track for the Direct WebRTC call. Held so type-to-speak
  // can restore live mic after a synth swap without re-prompting getUserMedia.
  const directOriginalMicRef = React.useRef<MediaStreamTrack | null>(null);

  const hangupDirectWebRTCCall = useCallback(() => {
    if (!directCallActiveRef.current && !directPcRef.current && !directSessionIdRef.current) return;
    // Invalidate callbacks before closing tracks/channels, which can fire their
    // own end events synchronously. A late microphone grant also sees this.
    directAttemptRef.current += 1;
    directCallActiveRef.current = false;
    const pc = directPcRef.current;
    const sessionId = directSessionIdRef.current;
    const cleanup = directCleanupRef.current;
    directPcRef.current = null;
    directSessionIdRef.current = null;
    directCleanupRef.current = null;
    cleanup?.();

    const videoDC = videoDCRef.current;
    if (videoDC) {
      videoDC.onclose = null;
      videoDC.onerror = null;
      videoDC.close();
      videoDCRef.current = null;
    }
    if (pc) {
      pc.close();
    }
    remoteAudioTrackRef.current = null;
    if (directOriginalMicRef.current) {
      try { directOriginalMicRef.current.stop(); } catch { /* ignore */ }
      directOriginalMicRef.current = null;
    }

    if (sessionId) {
      const runtimeConfig = sipRuntimeConfigRef.current;
      const mediaProxyUrl = runtimeConfig?.mediaProxyUrl || process.env.NEXT_PUBLIC_MEDIA_PROXY_URL || '/api/proxy/webrtc';
      if (mediaProxyUrl) {
        fetch(`${mediaProxyUrl}/hangup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          keepalive: true,
        }).catch(err => console.error('[DirectWebRTC] Hangup error:', err));
      }
    }

    setIsInCall(false);
    setIsCalling(false);
    setCallStatus(null);
    setConversationId(null);
    setRemoteAnalyserNode(null);
  }, []);

  useEffect(() => {
    window.addEventListener('pagehide', hangupDirectWebRTCCall);
    return () => {
      window.removeEventListener('pagehide', hangupDirectWebRTCCall);
      hangupDirectWebRTCCall();
    };
  }, [hangupDirectWebRTCCall]);

  // ============================================================================
  // LiveKit call refs + hangup (for BeyondPresence avatar — no DirectWebRTC)
  // ============================================================================

  const liveKitConversationIdRef = React.useRef<string | null>(null);

  const hangupLiveKitCall = useCallback(async () => {
    const conversationId = liveKitConversationIdRef.current;
    if (!conversationId) return;

    liveKitConversationIdRef.current = null;
    setIsInCall(false);
    setIsCalling(false);
    setCallStatus(null);
    setConversationId(null);

    // Close the gRPC session on conversation-api (stops the pipeline)
    try {
      await fetch('/api/livekit-call', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId }),
      });
    } catch (err) {
      console.error('[LiveKitCall] Hangup error:', err);
    }
  }, []);

  // ============================================================================
  // Hangup Call
  // ============================================================================

  const hangupCall = useCallback(() => {
    sipAttemptRef.current += 1;
    sipPreparationRef.current?.abort();
    sipPreparationRef.current = null;
    if (connectActiveRef.current) {
      connectCallRef.current.endCall();
      return;
    }
    // Check for LiveKit call first
    if (liveKitConversationIdRef.current) {
      hangupLiveKitCall();
      return;
    }

    // Check for direct WebRTC call
    if (directCallActiveRef.current || directPcRef.current || directSessionIdRef.current) {
      hangupDirectWebRTCCall();
      return;
    }

    if (!sipcallRef.current) {
      return;
    }

    try {
      if (sipcallRef.current._callTimeout) {
        clearTimeout(sipcallRef.current._callTimeout);
        delete sipcallRef.current._callTimeout;
      }
      const hangupRequest = { request: 'hangup' };
      sipcallRef.current.send({ message: hangupRequest });
      if (sipcallRef.current.hangup) {
        sipcallRef.current.hangup();
      }
      setConversationId(null);
      setIsCalling(false);
      setIsInCall(false);
      setCallStatus(null);
      remoteAudioTrackRef.current = null;
    } catch (error: any) {
      console.error('Hangup error:', error);
      setIsCalling(false);
      setIsInCall(false);
      setCallStatus(null);
      remoteAudioTrackRef.current = null;
    }
  }, [hangupDirectWebRTCCall, hangupLiveKitCall]);

  // ============================================================================
  // Logout
  // ============================================================================

  const logout = useCallback(async () => {
    connectCallRef.current.endCall();
    connectActiveRef.current = false;
    setConnectActive(false);
    // Hangup any active call
    if (isInCall || isCalling) {
      hangupCall();
    }

    // Clear conversation_id
    setConversationId(null);

    // Cleanup Janus
    if (janusRef.current) {
      janusRef.current.destroy();
      janusRef.current = null;
    }
    sipcallRef.current = null;

    // Call logout API and get SSO logout URL
    let ssoLogoutUrl: string | null = null;
    try {
      const response = await fetch(apiUrl('/api/auth/logout'), {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      ssoLogoutUrl = data.ssoLogoutUrl || null;
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
    }

    // Forget the tenant preference — a new login should start clean.
    try {
      localStorage.removeItem('activeTenantId');
    } catch {
      /* noop */
    }

    // Drop every cached API response so the next authenticated session
    // doesn't briefly see the previous user's picklist contents.
    bumpCacheEpoch();

    // Reset state
    setIsAuthenticated(false);
    setUser(null);
    setActiveTenant(null);
    setTenants([]);
    setIsPlatformAdmin(false);
    setSipCredentials(null);
    setIsRegistered(false);
    setIsRegistering(false);
    setIsCalling(false);
    setIsInCall(false);
    setCallStatus(null);
    sessionCreatedRef.current = false;
    initRef.current = false;

    // Redirect to SSO logout to clear SSO session (allows switching users)
    if (ssoLogoutUrl) {
      window.location.href = ssoLogoutUrl;
    }
  }, [isInCall, isCalling, hangupCall]);

  // ============================================================================
  // Direct WebRTC Call (voicebot test calls - bypass Janus/Asterisk)
  // ============================================================================

  const makeDirectWebRTCCall = useCallback(async (
    agentId: string,
    conversationId: string,
    pipelineVersion: string,
    callerName: string,
    customHeaders: Record<string, string>,
    overrideAudioTrack?: MediaStreamTrack,
  ) => {
    if (directCallActiveRef.current) return;
    // React state is not a lock: two calls in one event can both observe
    // isCalling=false before a render. Claim the attempt before any await.
    directCallActiveRef.current = true;
    const attempt = ++directAttemptRef.current;
    const isCurrent = () => directCallActiveRef.current && directAttemptRef.current === attempt;
    try {
      const runtimeConfig = sipRuntimeConfigRef.current;
      // mediaProxyUrl is either a relative path like "/api/proxy/webrtc" or full URL
      const mediaProxyUrl = runtimeConfig?.mediaProxyUrl || process.env.NEXT_PUBLIC_MEDIA_PROXY_URL || '/api/proxy/webrtc';
      if (!mediaProxyUrl) {
        throw new Error('Media proxy URL not configured for direct WebRTC');
      }

      // Build ICE servers (same TURN config as Janus path)
      const turnServer = runtimeConfig?.turnServer || process.env.NEXT_PUBLIC_TURN_SERVER || '';
      const turnUsername = runtimeConfig?.turnUsername || '';
      const turnCredential = runtimeConfig?.turnCredential || '';
      const forceTurnOnly = runtimeConfig?.forceTurnOnly ?? false;

      const iceServers: RTCIceServer[] = turnServer && turnUsername && turnCredential
        ? [
          { urls: [`stun:${turnServer}:3478`] },
          {
            urls: [
              `turn:${turnServer}:3478`,
              `turn:${turnServer}:3478?transport=tcp`,
              `turns:${turnServer}:5349?transport=tcp`,
              `turns:${turnServer}:443?transport=tcp`,
            ],
            username: turnUsername,
            credential: turnCredential,
          },
        ]
        : [{ urls: ['stun:stun.l.google.com:19302'] }];

      // Get audio source: either an injected synth track (e.g. type-to-speak) or
      // the live microphone. When a track is supplied we skip getUserMedia entirely
      // so the browser doesn't even prompt for mic permission.
      let stream: MediaStream;
      if (overrideAudioTrack) {
        console.log('[DirectWebRTC] Using injected audio track (synth mode):', overrideAudioTrack.id);
        stream = new MediaStream([overrideAudioTrack]);
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (!isCurrent()) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        // Remember the live mic so type-to-speak can swap back without re-prompting.
        directOriginalMicRef.current = stream.getAudioTracks()[0] || null;
      }

      // Create PeerConnection
      const pc = new RTCPeerConnection({
        iceServers,
        iceTransportPolicy: forceTurnOnly ? 'relay' : 'all',
      });
      directPcRef.current = pc;
      let mediaConnectionTimeout: ReturnType<typeof setTimeout> | null = null;
      let disconnectedTimeout: ReturnType<typeof setTimeout> | null = null;
      let finishGathering: (() => void) | null = null;
      let mediaConnected = false;
      let remoteAudioElement: HTMLAudioElement | null = null;
      let remoteAudioSource: MediaStreamAudioSourceNode | null = null;
      let stopDiagnostics: (() => void) | null = null;
      const clearMediaConnectionTimeout = () => {
        if (mediaConnectionTimeout !== null) clearTimeout(mediaConnectionTimeout);
        mediaConnectionTimeout = null;
      };
      directCleanupRef.current = () => {
        clearMediaConnectionTimeout();
        if (disconnectedTimeout !== null) clearTimeout(disconnectedTimeout);
        finishGathering?.();
        stopDiagnostics?.();
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.oniceconnectionstatechange = null;
        pc.onicegatheringstatechange = null;
        remoteAudioSource?.disconnect();
        if (remoteAudioElement) {
          remoteAudioElement.pause();
          remoteAudioElement.srcObject = null;
          remoteAudioElement = null;
        }
      };

      // Add audio track
      stream.getAudioTracks().forEach(track => pc.addTrack(track, stream));

      // Handle remote audio (TTS from AI) — guard against duplicate ontrack
      pc.ontrack = (ev) => {
        if (!isCurrent()) return;
        console.log('[DirectWebRTC] Remote track received:', ev.track.kind, 'streams:', ev.streams.length);
        if (ev.track.kind === 'audio') {
          remoteAudioTrackRef.current = ev.track;
          ev.track.addEventListener(
            'ended',
            () => {
              if (!isCurrent()) return;
              console.log('[DirectWebRTC] Remote audio track ended, ending call');
              hangupDirectWebRTCCall();
            },
            { once: true },
          );
        }
        if (remoteAudioElement) {
          console.log('[DirectWebRTC] IGNORING duplicate ontrack — already playing');
          return;
        }
        remoteAudioElement = new Audio();
        remoteAudioElement.srcObject = ev.streams[0];
        remoteAudioElement.play().catch(err => console.warn('[DirectWebRTC] Audio play failed:', err));

        // Create AnalyserNode from remote audio for avatar lip-sync
        try {
          if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
          const source = audioCtxRef.current.createMediaStreamSource(ev.streams[0]);
          remoteAudioSource = source;
          const analyser = audioCtxRef.current.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.5;
          source.connect(analyser);
          setRemoteAnalyserNode(analyser);
          console.log('[DirectWebRTC] AnalyserNode created for avatar lip-sync');
        } catch (err) {
          console.warn('[DirectWebRTC] Failed to create AnalyserNode:', err);
        }
      };

      pc.onconnectionstatechange = () => {
        if (!isCurrent()) return;
        console.log('[DirectWebRTC] Connection state:', pc.connectionState);
        if (pc.connectionState === 'connected') {
          mediaConnected = true;
          clearMediaConnectionTimeout();
          setIsInCall(true);
          setIsCalling(false);
          setCallStatus('Connected (Direct WebRTC)');
          stopDiagnostics?.();
          stopDiagnostics = startPreviewAudioDiagnostics(pc, conversationId, isCurrent);
          return;
        }
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          clearMediaConnectionTimeout();
          if (pc.connectionState === 'failed' && !mediaConnected) {
            toast.error('Preview audio could not connect. Please try the call again.');
          }
          console.log('[DirectWebRTC] Call ended (state:', pc.connectionState, ')');
          setIsInCall(false);
          setIsCalling(false);
          setCallStatus(null);
          setConversationId(null);
          // Clean up audio
          if (remoteAudioElement) {
            remoteAudioElement.srcObject = null;
            remoteAudioElement = null;
          }
          remoteAudioTrackRef.current = null;
          // Use the proper hangup function to clean up refs + notify media-proxy
          hangupDirectWebRTCCall();
        }
      };

      pc.oniceconnectionstatechange = () => {
        if (!isCurrent()) return;
        console.log('[DirectWebRTC] ICE state:', pc.iceConnectionState);
        if (pc.iceConnectionState === 'disconnected') {
          // ICE disconnected — remote side closed. Trigger hangup after brief delay
          // to avoid false positives from temporary network blips.
          if (disconnectedTimeout !== null) clearTimeout(disconnectedTimeout);
          disconnectedTimeout = setTimeout(() => {
            if (!isCurrent()) return;
            if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'closed') {
              console.log('[DirectWebRTC] ICE still disconnected after delay, ending call');
              setIsInCall(false);
              setIsCalling(false);
              setCallStatus(null);
              setConversationId(null);
              if (remoteAudioElement) {
                remoteAudioElement.srcObject = null;
                remoteAudioElement = null;
              }
              remoteAudioTrackRef.current = null;
              hangupDirectWebRTCCall();
            }
          }, 2000);
        }
      };

      // Create data channel for video frames (must be before createOffer so it's in the SDP).
      // Pre-negotiated (WARP): both ends create the channel on the agreed stream id, so
      // there is no in-band DCEP open/ack round trip and the channel is usable as soon as
      // SCTP connects. The id MUST match negotiatedVideoChannelID in media-proxy's
      // peer.go, and the offer below must set negotiated_video_channel so media-proxy
      // pre-creates its side instead of waiting for a DCEP open.
      const videoDC = pc.createDataChannel('video', {
        negotiated: true,
        id: NEGOTIATED_VIDEO_CHANNEL_ID,
        ordered: false,
        maxRetransmits: 0,
      });
      videoDC.binaryType = 'arraybuffer';
      videoDC.onclose = () => {
        if (!isCurrent()) return;
        console.log('[DirectWebRTC] Data channel closed, ending call');
        hangupDirectWebRTCCall();
      };
      videoDC.onerror = (event) => {
        if (!isCurrent()) return;
        console.warn('[DirectWebRTC] Data channel error, ending call:', event);
        hangupDirectWebRTCCall();
      };
      videoDCRef.current = videoDC;

      // Create offer
      const offer = await pc.createOffer();
      if (!isCurrent()) return;
      await pc.setLocalDescription(offer);
      if (!isCurrent()) return;

      // Wait for ICE gathering — TURN relay allocation through an NLB can take 1-2s.
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') { resolve(); return; }
        const timeout = setTimeout(() => finishGathering?.(), ICE_GATHERING_WAIT_MS);
        finishGathering = () => {
          clearTimeout(timeout);
          finishGathering = null;
          resolve();
        };
        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === 'complete') finishGathering?.();
        };
      });
      if (!isCurrent()) return;

      // Send offer to media-proxy
      // Remember the requested ID now so hangup also covers an in-flight offer.
      directSessionIdRef.current = conversationId;
      const resp = await fetch(`${mediaProxyUrl}/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sdp_offer: pc.localDescription?.sdp,
          agent_id: agentId,
          session_id: conversationId,
          pipeline_version: pipelineVersion,
          conversation_id: conversationId,
          call_source: 'ai-manager-test',
          caller_name: callerName,
          custom_headers: customHeaders,
          // Tells media-proxy we pre-negotiated the "video" channel above, so it
          // creates its matching side instead of waiting for a DCEP open.
          negotiated_video_channel: true,
        }),
      });

      if (!isCurrent()) {
        // The server may have created the session after our first hangup.
        if (resp.ok) {
          const { session_id } = await resp.json();
          await fetch(`${mediaProxyUrl}/hangup`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: session_id || conversationId }),
            keepalive: true,
          }).catch(() => {});
        }
        return;
      }
      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(`WebRTC offer failed: ${errText}`);
      }

      const { sdp_answer, session_id } = await resp.json();
      if (!isCurrent()) {
        await fetch(`${mediaProxyUrl}/hangup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: session_id || conversationId }),
          keepalive: true,
        }).catch(() => {});
        return;
      }
      directSessionIdRef.current = session_id;

      // Set remote description
      await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: sdp_answer }));
      if (!isCurrent()) return;
      // Signaling success does not mean media can flow. Wait for ICE/DTLS before
      // starting the call timer or presenting the call as connected.
      if (!mediaConnected) {
        mediaConnectionTimeout = setTimeout(() => {
          if (mediaConnected || !isCurrent()) return;
          toast.error('Preview audio could not connect. Please try the call again.');
          setIsInCall(false);
          setIsCalling(false);
          setCallStatus(null);
          setConversationId(null);
          hangupDirectWebRTCCall();
        }, 20_000);
      }
    } catch (error) {
      if (!isCurrent()) return;
      hangupDirectWebRTCCall();
      throw error;
    }
  }, [hangupDirectWebRTCCall]);

  // ============================================================================
  // LiveKit Call (BeyondPresence avatar — single connection, no DirectWebRTC)
  // ============================================================================

  const makeLiveKitCall = useCallback(async (
    agentId: string,
    conversationId: string,
    pipelineVersion: string,
    customHeaders: Record<string, string>,
  ) => {
    console.log('[LiveKitCall] Starting LiveKit-only session for Bey avatar');

    const resp = await fetch('/api/livekit-call', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agent_id: agentId,
        conversation_id: conversationId,
        pipeline_version: pipelineVersion,
        custom_headers: customHeaders,
      }),
    });

    if (!resp.ok) {
      const errText = await resp.text();
      throw new Error(`LiveKit session failed: ${errText}`);
    }

    const data = await resp.json();
    const liveKitConversationId = data.conversation_id || conversationId;
    liveKitConversationIdRef.current = liveKitConversationId;
    setConversationId(liveKitConversationId);

    console.log('[LiveKitCall] Session created:', data.session_id, 'conversation:', liveKitConversationId);
    setIsInCall(true);
    setIsCalling(false);
    setCallStatus('Connected (LiveKit)');
  }, []);

  // ============================================================================
  // Make Call (routes to LiveKit for Bey, direct WebRTC for voicebots, Janus SIP for translation)
  // ============================================================================

  const makeCall = useCallback(async (extension: string, options?: CallOptions) => {
    if (directCallActiveRef.current || isInCallRef.current || isCallingRef.current) return;
    const telephony = options?.telephony;
    if (telephony?.transport === 'amazon_connect_webrtc') {
      setTelephonyConversationId(null);
      setTelephonySessionId(null);
      connectActiveRef.current = true;
      setConnectActive(true);
      await connectCallRef.current.startCall({
        integrationId: telephony.integrationId,
        contactFlowId: telephony.contactFlowId,
        participantDisplayName: user?.displayName || user?.username || 'Preview caller',
        preview: { smsDeliveryMode: telephony.smsDeliveryMode ?? 'demo_notification' },
        attributes: { ...telephony.attributes, ...(telephony.ani ? { ani: telephony.ani, caller_id_number: telephony.ani } : {}) },
      });
      return;
    }
    connectActiveRef.current = false;
    setConnectActive(false);
    // Route voicebot pipelines to direct WebRTC (bypass Janus/Asterisk for Opus quality)
    const pipelineType = options?.agentConfig?.pipeline_type;
    const isVoicebot = !telephony && (pipelineType === 'speech_to_speech' || pipelineType === 'voice_ai' || !pipelineType);
    const runtimeConfig = sipRuntimeConfigRef.current;
    // WebRTC proxy routes are always available (/api/proxy/webrtc/*). Media-proxy
    // returns an error if WEBRTC_ENABLED=false, so we always try the direct path.
    const hasMediaProxy = true;

    // Detect BeyondPresence avatar — route to LiveKit-only path (single WebRTC connection)
    const avatarProvider = options?.agentConfig?.settings?.avatar?.provider;
    const isBeyAvatar = avatarProvider === 'bey' && options?.agentConfig?.settings?.avatar?.enabled;

    if (isVoicebot && isBeyAvatar) {
      console.log('[AuthContext] 🎬 Using LiveKit-only path for BeyondPresence avatar call');
      if (isInCallRef.current || isCallingRef.current) {
        return;
      }
      const newConversationId = crypto.randomUUID();
      setConversationId(newConversationId);
      setIsCalling(true);
      setCallStatus('Connecting (LiveKit)...');
      try {
        const customHeaders = withDemoNotificationSession(
          options?.customHeaders,
          newConversationId,
        );
        const pipelineVersion = customHeaders['X-Pipeline-Version'] || 'draft';
        await makeLiveKitCall(
          extension.trim(),
          newConversationId,
          pipelineVersion,
          customHeaders,
        );
      } catch (err: any) {
        console.error('[AuthContext] LiveKit call failed:', err);
        setIsCalling(false);
        setCallStatus(null);
        setConversationId(null);
        toast.error(`LiveKit call failed: ${err.message || err}`);
      }
      return;
    }

    if (isVoicebot && hasMediaProxy) {
      console.log('[AuthContext] 🎙️ Using direct WebRTC (Opus) for voicebot test call');
      if (isInCallRef.current || isCallingRef.current) {
        return;
      }
      const newConversationId = crypto.randomUUID();
      setConversationId(newConversationId);
      setIsCalling(true);
      setCallStatus('Connecting (Direct WebRTC)...');
      try {
        const customHeaders = withDemoNotificationSession(
          options?.customHeaders,
          newConversationId,
        );
        const pipelineVersion = customHeaders['X-Pipeline-Version'] || 'draft';
        await makeDirectWebRTCCall(
          extension.trim(),
          newConversationId,
          pipelineVersion,
          user?.displayName || user?.username || 'Test User',
          customHeaders,
          options?.audioTrack,
        );
      } catch (err: any) {
        console.error('[AuthContext] Direct WebRTC call failed:', err);
        setIsCalling(false);
        setCallStatus(null);
        setConversationId(null);
        toast.error(`Direct call failed: ${err.message || err}. Please try again.`);
      }
      return;
    }

    console.log('[AuthContext] 📞 Using Janus SIP path for call (pipeline_type=' + pipelineType + ')');

    // Ensure SIP is registered (use ref to get current value)
    if (!isRegisteredRef.current) {
      const success = await registerSIP();
      if (!success) {
        toast.error('SIP registration required to make calls');
        return;
      }
    }

    if (!sipcallRef.current) {
      toast.error('SIP not ready. Please try again.');
      return;
    }

    if (isInCallRef.current || isCallingRef.current) {
      return;
    }

    if (!window.Janus?.isWebrtcSupported?.()) {
      toast.error('WebRTC not supported');
      return;
    }

    if (janusRef.current?.isConnected && !janusRef.current.isConnected()) {
      toast.error('Connection lost. Please log in again.');
      setIsRegistered(false);
      return;
    }

    const newConversationId = crypto.randomUUID();
    // The external SIP trace ID is not a TrueServe conversation. Keep it off
    // editor observers and the page's "last preview conversation" memory.
    setConversationId(telephony ? null : newConversationId);
    setTelephonyConversationId(null);
    setTelephonySessionId(null);

    const customHeaders = withDemoNotificationSession(
      telephony ? undefined : options?.customHeaders,
      newConversationId,
    );
    const routingMode = telephony ? 'kamailio' : options?.routingMode || 'kamailio';

    // External dialing always uses the configured Asterisk proxy. The server
    // replaces this placeholder with the validated number before the offer.
    let sipUri = telephony ? 'telephony-preview' : extension.trim();

    if (routingMode === 'direct' && sipUri.indexOf('sip:direct-') === 0) {
      const match = sipUri.match(/sip:direct-(.+)@/);
      if (match) {
        sipUri = `sip:${match[1]}@127.0.0.1:5063`;
      }
    } else if (sipUri.indexOf('sip:') === 0 && (sipUri.includes('trueserve-pbx.byoc.usw2.pure.cloud') || sipUri.includes('trueserve-framework-vanguard.byoc.usw2.pure.cloud'))) {
      const match = sipUri.match(/sip:([^@]+)@/);
      if (match) {
        sipUri = `sip:${match[1]}@127.0.0.1:5063`;
      }
    } else if (sipUri.indexOf('sip:') !== 0) {
      // Use runtime config from server (same source as registerSIP), then build-time env, then fallback
      const runtimeConfig = sipRuntimeConfigRef.current;
      const asteriskProxyEnv = runtimeConfig?.asteriskProxy || process.env.NEXT_PUBLIC_ASTERISK_PROXY || '';
      let asteriskHost = '127.0.0.1';
      let asteriskPort = '5063';

      if (asteriskProxyEnv) {
        const clean = asteriskProxyEnv.replace(/^sip:/, '');
        const parts = clean.split(':');
        asteriskHost = parts[0];
        asteriskPort = parts[1] || '5063';
      }

      sipUri = `sip:${sipUri}@${asteriskHost}:${asteriskPort}`;
    }

    const attempt = ++sipAttemptRef.current;
    const preparation = new AbortController();
    sipPreparationRef.current = preparation;
    try {
      const handle = sipcallRef.current;

      if (!handle || typeof handle.createOffer !== 'function') {
        toast.error('SIP handle not ready. Please try again.');
        setConversationId(null);
        return;
      }

      setIsCalling(true);
      setCallStatus('Calling...');

      // Mint the internal-leg sig server-side. ari-controller's
      // verifyInternalLeg() rejects browser-tagged callSources without it
      // (see apps/ari-controller-node/src/services/ccaas-detector.ts). Best-
      // effort: a 4xx/5xx here means we can't prove the call is internal, so
      // we surface the failure rather than letting Asterisk reject silently.
      let internalLegSig: string | null = null;
      if (telephony?.transport === 'sip') {
        // Resolve the tenant's configured trunk server-side; a number is not an
        // internal AI-agent extension and must never request an internal-leg sig.
        const response = await fetch('/api/preview/telephony/sip-target', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(telephony),
          signal: preparation.signal,
        });
        const prepared = await response.json();
        if (!response.ok) throw new Error(prepared.error || 'Could not prepare telephony call.');
        // Keep the Asterisk host resolved above and use only the validated number.
        sipUri = sipUri.replace(/^sip:[^@]+@/, `sip:${prepared.destination}@`);
        Object.assign(customHeaders, prepared.headers);
        if (attempt === sipAttemptRef.current && typeof prepared.previewSessionId === 'string') {
          setTelephonySessionId(prepared.previewSessionId);
        }
      } else {
        try {
          const sigResp = await fetch('/api/internal-leg-sig', {
            method: 'POST',
            credentials: 'include',
            signal: preparation.signal,
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              callSource: 'ai-manager-test',
              convId: newConversationId,
              extension: extension.trim(),
              uui: customHeaders['User-to-User'] || '',
            }),
          });
          if (sigResp.ok) {
            const sigBody = await sigResp.json();
            internalLegSig = sigBody?.sig || null;
          } else {
            const detail = await sigResp.text().catch(() => '');
            console.warn('[AuthContext] internal-leg-sig fetch non-OK:', sigResp.status, detail);
          }
        } catch (err) {
          if (attempt === sipAttemptRef.current) console.warn('[AuthContext] internal-leg-sig fetch failed:', err);
        }
        if (attempt !== sipAttemptRef.current) return;
        if (!internalLegSig) {
          toast.error('Could not authorize test call. Please retry.');
          setIsCalling(false);
          setCallStatus(null);
          setConversationId(null);
          return;
        }
      }
      if (attempt !== sipAttemptRef.current) return;

      // Use the supplied synth track if provided (type-to-speak mode); otherwise
      // let Janus.js call getUserMedia with standard mic constraints.
      const audioCapture = options?.audioTrack
        ? options.audioTrack
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
      console.log('[AuthContext] 🎤 Creating offer with audio capture...',
        options?.audioTrack ? '(injected synth track)' : '(live mic)');
      handle.createOffer({
        tracks: [{ type: 'audio', capture: audioCapture, recv: true }],
        success: (jsep: any) => {
          if (attempt !== sipAttemptRef.current || handle !== sipcallRef.current) return;
          sipAudioDiagnosticsRef.current?.();
          sipAudioDiagnosticsRef.current = null;
          if (telephony?.transport === 'sip' && handle.webrtcStuff?.pc) {
            const pc = handle.webrtcStuff.pc as RTCPeerConnection;
            sipAudioDiagnosticsRef.current = startPreviewAudioDiagnostics(
              pc,
              newConversationId,
              () => attempt === sipAttemptRef.current && handle === sipcallRef.current
                && pc === handle.webrtcStuff?.pc,
            );
          }
          console.log('[AuthContext] 🎤 Offer created successfully, checking local stream...');
          // Debug: Check if we have a local stream with audio tracks
          if (handle.webrtcStuff?.myStream) {
            const audioTracks = handle.webrtcStuff.myStream.getAudioTracks();
            console.log('[AuthContext] 🎤 Local audio tracks:', audioTracks.length);
            audioTracks.forEach((track: MediaStreamTrack, i: number) => {
              console.log(`[AuthContext] 🎤 Audio track ${i}:`, {
                id: track.id,
                label: track.label,
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState
              });
            });
          } else {
            console.warn('[AuthContext] ⚠️ No local stream found after createOffer!');
          }
          const headers: any = {
            'X-Conversation-Id': newConversationId,
            ...(telephony ? {} : {
              'X-Call-Source': 'ai-manager-test',
              'X-Caller-Name': user?.displayName || user?.username || 'Test User',
              'X-Internal-Leg-Sig': internalLegSig,
            }),
          };

          if (routingMode === 'direct') {
            headers['X-Routing-Mode'] = 'direct';
          }

          // Merge caller-provided headers (e.g. X-Pipeline-Id, X-Test-Pipeline-Id, etc.)
          Object.assign(headers, customHeaders);

          // Debug log the headers being sent
          console.log('[AuthContext] 📞 makeCall - headers being sent to Janus:', JSON.stringify(headers, null, 2));
          console.log('[AuthContext] 📞 makeCall - customHeaders received:', JSON.stringify(customHeaders, null, 2));

          const callRequest = {
            request: 'call',
            uri: sipUri,
            autoaccept_reinvites: false,
            // Janus bridges browser Opus to a plain SIP leg here. Prefer PCMU on
            // that SIP leg because Asterisk/Kamailio test routes accept it reliably.
            audiocodec: 'pcmu',
            headers: headers
          };

          console.log('[AuthContext] 📞 makeCall - full callRequest:', JSON.stringify(callRequest, null, 2));

          handle.send({ message: callRequest, jsep: jsep });

          const callTimeout = setTimeout(() => {
            setIsCalling(false);
            setCallStatus(null);
            setConversationId(null);
            toast.error('Call timeout: No response from destination.');
            if (sipcallRef.current?.hangup) {
              try {
                sipcallRef.current.hangup();
              } catch (err) {
                console.error('Error hanging up timed-out call:', err);
              }
            }
          }, 60000);

          (handle as any)._callTimeout = callTimeout;
        },
        error: (error: any) => {
          if (attempt !== sipAttemptRef.current) return;
          console.error('Failed to create offer:', error);
          setIsCalling(false);
          setCallStatus(null);
          setConversationId(null);
          toast.error(`Failed to create call: ${error.message || error}`);
        }
      });
    } catch (error: any) {
      if (attempt !== sipAttemptRef.current) return;
      console.error('Call error:', error);
      setIsCalling(false);
      setCallStatus(null);
      setConversationId(null);
      toast.error(`Call failed: ${error.message || error}`);
    }
  }, [registerSIP, makeLiveKitCall, user?.displayName, user?.username]); // Call state reads refs for stability

  // ============================================================================
  // Send DTMF
  // ============================================================================

  // Each transport has its own way in, and picking the wrong one fails SILENTLY.
  //
  //  - Direct WebRTC (agent test calls) has no SIP leg at all, so a digit can
  //    only reach the pipeline out of band, through media-proxy.
  //  - Janus SIP sends SIP INFO. The in-band `handle.dtmf()` alternative needs a
  //    DTMF-capable audio sender on that handle; when there isn't one janus only
  //    warns ("Invalid DTMF configuration") and calls an error callback that
  //    defaults to `Janus.noop` — no throw, no digit, nothing in the UI. Which
  //    is exactly what happened when this preferred `handle.dtmf()`: keypad
  //    presses during a WebRTC test call went to the registration handle and
  //    vanished, and secure payment capture timed out on an empty field.
  const sendDTMF = useCallback((digit: string) => {
    if (connectActiveRef.current) {
      connectCallRef.current.sendDTMF?.(digit);
      return;
    }
    if (!isInCall) {
      return;
    }

    const directSessionId = directSessionIdRef.current;
    if (directSessionId) {
      const runtimeConfig = sipRuntimeConfigRef.current;
      const mediaProxyUrl =
        runtimeConfig?.mediaProxyUrl || process.env.NEXT_PUBLIC_MEDIA_PROXY_URL || '/api/proxy/webrtc';
      fetch(`${mediaProxyUrl}/dtmf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: directSessionId, digit }),
      })
        .then((resp) => {
          if (!resp.ok) {
            console.error('[DirectWebRTC] DTMF rejected:', resp.status);
            toast.error(`Failed to send ${digit}: media proxy returned ${resp.status}`);
          }
        })
        .catch((err) => {
          console.error('[DirectWebRTC] DTMF error:', err);
          toast.error(`Failed to send ${digit}`);
        });
      return;
    }

    if (liveKitConversationIdRef.current) {
      console.warn('DTMF is not supported on the LiveKit avatar transport');
      toast.error('This call type does not support keypad digits');
      return;
    }

    if (!sipcallRef.current) {
      return;
    }

    try {
      sipcallRef.current.send({ message: { request: 'dtmf_info', digit } });
    } catch (error: any) {
      console.error('DTMF error:', error);
      toast.error(`Failed to send DTMF: ${error.message || error}`);
    }
  }, [isInCall]);

  const sendVideoFrame = useCallback((data: ArrayBuffer) => {
    const dc = videoDCRef.current;
    if (dc && dc.readyState === 'open') {
      dc.send(data);
    }
  }, []);

  // ============================================================================
  // Get Local Audio Track (powers local-side waveform)
  // ============================================================================

  const getLocalAudioTrack = useCallback((): MediaStreamTrack | null => {
    if (connectActiveRef.current) return connectCallRef.current.getLocalAudioTrack();
    const pc = directPcRef.current;
    if (pc) {
      const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
      if (sender?.track) return sender.track;
    }
    const handle = sipcallRef.current;
    const myStream = handle?.webrtcStuff?.myStream;
    if (myStream && typeof myStream.getAudioTracks === 'function') {
      const tracks = myStream.getAudioTracks();
      if (tracks.length > 0) return tracks[0];
    }
    return null;
  }, []);

  const getRemoteAudioTrack = useCallback((): MediaStreamTrack | null => {
    if (connectActiveRef.current) return connectCallRef.current.getRemoteAudioTrack();
    return remoteAudioTrackRef.current;
  }, []);

  // ============================================================================
  // Replace Local Audio (mid-call mic ↔ synth swap)
  // ============================================================================

  const replaceLocalAudio = useCallback(async (track: MediaStreamTrack | null): Promise<boolean> => {
    if (connectActiveRef.current) return connectCallRef.current.replaceLocalAudio(track);
    // Direct WebRTC: swap on the existing RTCRtpSender. No renegotiation needed.
    const pc = directPcRef.current;
    if (pc) {
      const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
      if (!sender) {
        console.warn('[ReplaceAudio] No audio sender on direct PC');
        return false;
      }
      const next = track ?? directOriginalMicRef.current;
      if (!next) {
        console.warn('[ReplaceAudio] No track to restore (original mic missing)');
        return false;
      }
      try {
        await sender.replaceTrack(next);
        console.log('[ReplaceAudio] Direct WebRTC sender now using', next.id, '(label:', next.label || '<synth>', ')');
        return true;
      } catch (err) {
        console.error('[ReplaceAudio] sender.replaceTrack failed:', err);
        return false;
      }
    }

    // Janus SIP: use replaceTracks. When restoring (`null`), re-prompt the mic
    // via standard constraints so we don't try to reuse a stopped track.
    const handle = sipcallRef.current;
    if (handle && typeof handle.replaceTracks === 'function') {
      const capture: any = track
        ? track
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
      return await new Promise<boolean>((resolve) => {
        try {
          handle.replaceTracks({
            tracks: [{ type: 'audio', capture, replace: true }],
            success: () => {
              console.log('[ReplaceAudio] Janus replaceTracks ok',
                track ? '(synth)' : '(mic)');
              resolve(true);
            },
            error: (err: any) => {
              console.error('[ReplaceAudio] Janus replaceTracks failed:', err);
              resolve(false);
            },
          });
        } catch (err) {
          console.error('[ReplaceAudio] Janus replaceTracks threw:', err);
          resolve(false);
        }
      });
    }

    console.warn('[ReplaceAudio] No active call to swap audio on');
    return false;
  }, []);

  // ============================================================================
  // Tenant Switching
  // ============================================================================

  const switchTenant = useCallback(async (tenantId: string) => {
    try {
      const response = await fetch(apiUrl('/api/auth/switch-tenant'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId }),
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to switch tenant');
      }

      const data = await response.json();
      setUser(data.user);
      setActiveTenant(data.user.activeTenant);
      setTenants(data.user.tenants);
      setIsPlatformAdmin(data.user.isPlatformAdmin || false);

      // Remember the explicit tenant choice so a page refresh doesn't bounce
      // back to tenants[0]. The server also stores it in the session row,
      // but that row is thrown away when SSO re-auths.
      try {
        if (data.user.activeTenant?.id) {
          localStorage.setItem('activeTenantId', data.user.activeTenant.id);
        }
      } catch {
        /* noop */
      }

      // Invalidate every API cache entry — integrations, models, voices,
      // etc. are tenant-scoped server-side so serving stale-per-tenant
      // picklists would leak data across tenants.
      bumpCacheEpoch();

      // Reload a tenant-wide library, dropping the previous agent and chat.
      // The focused Analytics app stays in its own library across tenants.
      const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
      const inAnalytics = window.location.pathname.replace(/\/$/, '') === `${basePath}/analytics`;
      const focused = new URLSearchParams(window.location.search).get('focus') === '1';
      const destination = inAnalytics ? `/analytics${focused ? '?focus=1' : ''}` : '/agents';
      window.location.href = `${basePath}${destination}`;
    } catch (error: any) {
      console.error('[AuthContext] Tenant switch failed:', error);
      toast.error(error.message || 'Failed to switch tenant');
    }
  }, []);

  // ============================================================================
  // Provider Value
  // ============================================================================

  return (
    <AuthContext.Provider
      value={{
        // Auth state
        isAuthenticated,
        isAuthLoading,
        user,

        // Permission helpers
        hasPermission,
        hasAnyPermission,

        // SIP state
        sipCredentials,
        isSipLoading,
        janusReady,
        isRegistered,
        isRegistering,
        isCalling: connectActive ? connectCall.status === 'calling' : isCalling,
        isInCall: connectActive ? connectCall.status === 'connected' : isInCall,
        callStatus: connectActive ? connectCall.error || connectCall.status : callStatus,
        callError: connectActive ? connectCall.error : null,
        conversationId: connectActive ? null : conversationId,
        telephonyConversationId,
        callStartedAt,
        telephonyNotificationSessionId: connectActive ? connectCall.notificationSessionId : null,
        setConversationId,
        remoteAnalyserNode: connectActive ? connectCall.analyserNode : remoteAnalyserNode,

        // Tenant state
        activeTenant,
        tenants,
        isPlatformAdmin,
        switchTenant,

        // Auth actions
        login,
        logout,

        // SIP actions
        fetchSipCredentials,
        registerSIP,
        makeCall,
        hangupCall,
        sendDTMF,
        sendVideoFrame,
        replaceLocalAudio,
        getLocalAudioTrack,
        getRemoteAudioTrack,

        // Refs
        janusRef,
        sipcallRef,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================================================
// Type Declarations
// ============================================================================

declare global {
  interface Window {
    Janus: any;
    adapter: any;
  }
}
